import { defaultTheme } from 'react-admin';
import { createTheme } from '@mui/material/styles';
import { alpha } from '@mui/material';

import variables from './variables';

const lightTheme = createTheme({
	...defaultTheme,
	palette: {
		primary: {
			main: variables.colors.primary
		},
		secondary: {
			main: variables.colors.secondary
		},
		success: {
			main: variables.colors.success
		},
		error: {
			main: variables.colors.error
		},
		warning: {
			main: variables.colors.warning
		},
		info: {
			main: variables.colors.info
		},
		background: {
			default: '#F6FCFF'
		}
	},
	typography: {
		fontFamily: 'Inter',
		h1: { fontFamily: 'Outfit' },
		h2: { fontFamily: 'Outfit' },
		h3: { fontFamily: 'Outfit' },
		h4: { fontFamily: 'Outfit' },
		h5: { fontFamily: 'Outfit' },
		h6: { fontFamily: 'Outfit' },
		subtitle1: { fontFamily: 'Outfit' },
	},
	sidebar: {
		closedWidth: 55
	},
	components: {
		...defaultTheme.components,
		MuiOutlinedInput: {
			styleOverrides: {
				notchedOutline: {
					borderColor: '#000'
				}
			}
		},
		MuiFormLabel: {
			styleOverrides: {
				root: {
					color: '#000'
				}
			}
		},
		MuiAppBar: {
			styleOverrides: {
				root: {
					boxShadow: 'none',
					zIndex: 1300,
					color: '#FFF',
					'& .search': {
						display: 'none',
						'& + span': {
							marginLeft: '15px'
						},
						'@media (min-width: 600px)': {
							display: 'block'
						}
					}
				}
			}
		},
		MuiDrawer: {
			styleOverrides: {
				docked: {
					marginRight: '19px',
					'& ul': {
						height: 'calc(100% - 48px)'
					},
					'& .build-date': {
						padding: '5px',
						justifySelf: 'flex-end',
						alignSelf: 'center',
						marginTop: 'auto'
					}
				}
			}
		},
		MuiFormControl: {
			defaultProps: {
				variant: 'outlined'
			},
			styleOverrides: {
				root: {
					minWidth: '250px'
				}
			}
		},
		MuiAutocomplete: {
			styleOverrides: {
				root: {
					minWidth: '250px'
				}
			}
		},
		RaSelectInput: {
			styleOverrides: {
				root: {
					minWidth: '250px'
				}
			}
		},
		MuiTextField: {
			defaultProps: {
				variant: 'outlined',
			},
		},
		MuiSnackbarContent: {
			styleOverrides: {
				message: {
					whiteSpace: 'pre-wrap'
				}
			}
		},
		MuiMenuItem: {
			styleOverrides: {
				root: {
					'@media (min-width: 600px)': {
						minHeight: '36px'
					}
				}
			}
		},
		MuiInputBase: {
			defaultProps: {
				autoComplete: 'off'
			}
		},
		MuiCardHeader: {
			styleOverrides: {
				root: {
					borderBottom: '1px solid #DADCE0',
				},
				title: {
					fontSize: '22px'
				}
			}
		},
		MuiButton: {
			styleOverrides: {
				contained: {
					borderRadius: '28px',
					fontSize: '14px',
					boxShadow: 'none',
					'&:hover': {
						boxShadow: 'none'
					}
				},
				containedPrimary: {
					color: '#FFF'
				}
			}
		},
		RaRichTextInput: {
			styleOverrides: {
				root: {
					maxWidth: '100%',
					'& .RaRichTextInput-editorContent': {
						'& > div': {
							minHeight: '300px',
						}
					},
					'& .RaRichTextInput-editorContent img': {
						maxWidth: '100%'
					},
					'& .RaRichTextInputToolbar-root': {
						alignSelf: 'flex-start'
					},
					'& .ProseMirror': {
						borderColor: '#000 !important'
					}
				}
			}
		},
		RaFilterForm: {
			styleOverrides: {
				root: {
					display: 'flex',
					alignItems: 'center',
					paddingBottom: '8px',
					'& .RaFilterForm-filterFormInput, & .RaFilterForm-filterFormInput .MuiFormControl-root': {
						margin: 0,
						'& .RaFilterFormInput-hideButton': {
							marginBottom: 0,
							marginRight: '5px',
							'& + div': {
								alignSelf: 'center'
							}
						}
					},
					'& .MuiFormControl-root, & .MuiAutocomplete-root, & .RaSelectInput-root': {
						minWidth: '160px'
					}
				}
			}
		},
		RaEdit: {
			styleOverrides: {
				root: {
					'& .MuiCard-root': {
						boxShadow: 'none',
						border: '1px solid #000',
						marginBottom: '15px'
					}
				}
			}
		},
		RaCreate: {
			styleOverrides: {
				root: {
					'& .MuiCard-root': {
						boxShadow: 'none',
						border: '1px solid #000',
						marginBottom: '15px'
					}
				}
			}
		},
		RaMenu: {
			styleOverrides: {
				root: {
					'& .MuiMenuItem-root': {
						margin: '5px 10px',
						borderRadius: '200px',
						color: '#000',
						'& svg': {
							color: '#000'
						},
						'&.RaMenuItemLink-active, &:hover': {
							backgroundColor: alpha(variables.colors.primary, 0.1) + '!important',
							color: variables.colors.primary,
							transition: 'all 0.3s ease',
							'& svg': {
								color: variables.colors.primary,
								transition: 'all 0.3s ease',
							}
						}
					},
					'&.RaMenu-closed': {
						'& .MuiMenuItem-root': {
							margin: '5px 0 !important'
						},
						'& .RaMenuItemLink-active, & .MuiMenuItem-root:hover': {
							backgroundColor: 'transparent !important',
							color: variables.colors.primary,
							'& svg': {
								color: variables.colors.primary
							}
						}
					}
				}
			}
		},
		MuiCssBaseline: {
			styleOverrides: {
				'.rich-text': {
					'& img': {
						maxWidth: '100%'
					}
				}
			}
		}
	}
});

export default lightTheme;